import { namespacedKey } from "@/store/tabStore/identity";
import { SHARED_DB_NAME, STORE_NAME } from "@/store/tabStore/keys";
import { tabStorageReadyGate } from "@/store/tabStore/readyGate";
import { AsyncStorage } from "@/store/tabStore/types";
import { safeAsync } from "@/store/tabStore/util";

import localforage from "localforage";

// The single localforage instance both facades write to.
localforage.config({
  name: SHARED_DB_NAME,
  storeName: STORE_NAME
});

/** Global (non-namespaced) storage. Concrete localforage type so it can also back the RQ persister. */
export const sharedStorage = localforage;

/** Per-tab storage: auto-namespaces keys and waits for the boot-readiness gate. */
export const tabStorage: AsyncStorage = {
  async getItem<T>(key: string): Promise<T | null> {
    await tabStorageReadyGate.whenReady();
    return sharedStorage.getItem<T>(namespacedKey(key));
  },
  async setItem<T>(key: string, value: T): Promise<T> {
    await tabStorageReadyGate.whenReady();
    return sharedStorage.setItem<T>(namespacedKey(key), value);
  },
  async removeItem(key: string): Promise<void> {
    await tabStorageReadyGate.whenReady();
    return sharedStorage.removeItem(namespacedKey(key));
  }
};

// Safe wrappers for internal bookkeeping keys: thin `safeAsync` adapters that
// never throw, so a storage hiccup can't break boot or logout.

/** getItem that never throws — null on failure. */
export const safeGetItem = <T>(key: string): Promise<T | null> =>
  safeAsync(() => sharedStorage.getItem<T>(key), `getItem ${key}`);

/** setItem that never throws — true if the write succeeded. */
export const safeSetItem = async <T>(key: string, value: T): Promise<boolean> =>
  (await safeAsync(async () => {
    await sharedStorage.setItem(key, value);
    return true;
  }, `setItem ${key}`)) ?? false;

/** removeItem that never throws. */
export const safeRemoveItem = async (key: string): Promise<void> => {
  await safeAsync(() => sharedStorage.removeItem(key), `removeItem ${key}`);
};

/** All physical keys in the DB (AsyncStorage omits keys(), so reach localforage). */
export const listKeys = async (): Promise<string[]> =>
  (await safeAsync(() => localforage.keys(), "keys()")) ?? [];
